import React from 'react';
import { connect } from 'react-redux';
import { Modal, Form, Input, Icon, Switch, Badge, Tooltip, Divider, Button, Tree } from 'antd';

import * as permissionsActions from '../actions';
import { permissionsTree } from '~/components/Permissions/permissions-tree';

const FormItem = Form.Item;

class PermissionsFormModal extends React.Component {
  state = {
    isSaving: false,
    checkedKeys: [],
    selectedModule: null,
    expandedSections: {}
  };

  componentDidUpdate(prevProps) {
    if (this.props.modal.isVisible && !prevProps.modal.isVisible) {
      var data = this.props.modal.data;
      var initialPermissions = data ? data.permissions : [];

      // Sócio (ID 1) always has all permissions
      if (data && data.id === 1) {
        var user = this.props.user;
        var isPremiumPlan = user.tenant.plan === 'premium';
        var allowBetaFeatures = user.tenant.is_beta_tester;
        var modules = this.getTopLevelModules(permissionsTree, isPremiumPlan, allowBetaFeatures);
        initialPermissions = this.getAllKeys(modules);
      }

      this.setState({
        checkedKeys: initialPermissions || [],
        selectedModule: null,
        expandedSections: {}
      });
    }
  }

  getAllKeys = (items) => {
    var keys = [];
    var self = this;
    items.forEach(function(item) {
      if (item.key.indexOf('_PARENT') === -1 && item.key.indexOf('_SECTION') === -1) {
        keys.push(item.key);
      }
      if (item.children) {
        keys = keys.concat(self.getAllKeys(item.children));
      }
    });
    return keys;
  };

  filterByPlan = (items, isPremiumPlan, allowBetaFeatures) => {
    var self = this;
    return items.filter(function(item) {
      if (!isPremiumPlan && item.plans && item.plans.indexOf('starter') === -1) {
        return false;
      }
      if (!allowBetaFeatures && item.isBeta) {
        return false;
      }
      return true;
    }).map(function(item) {
      if (item.children) {
        return Object.assign({}, item, {
          children: self.filterByPlan(item.children, isPremiumPlan, allowBetaFeatures)
        });
      }
      return item;
    });
  };

  getTopLevelModules = (items, isPremiumPlan, allowBetaFeatures) => {
    return this.filterByPlan(items, isPremiumPlan, allowBetaFeatures);
  };

  isChecked = (key) => {
    return this.state.checkedKeys.indexOf(key) > -1;
  };

  togglePermission = (key) => {
    var currentChecked = this.state.checkedKeys.slice();
    var index = currentChecked.indexOf(key);
    if (index > -1) {
      currentChecked.splice(index, 1);
    } else {
      currentChecked.push(key);
    }
    this.setState({ checkedKeys: currentChecked });
  };

  toggleAllInModule = (module, checked) => {
    var items = module.children || [module];
    var allKeys = this.getAllKeys(items);
    var currentChecked = this.state.checkedKeys.slice();

    if (checked) {
      allKeys.forEach(function(key) {
        if (currentChecked.indexOf(key) === -1) {
          currentChecked.push(key);
        }
      });
    } else {
      currentChecked = currentChecked.filter(function(key) {
        return allKeys.indexOf(key) === -1;
      });
    }

    this.setState({ checkedKeys: currentChecked });
  };

  getModuleStats = (module) => {
    var items = module.children || [module];
    var allKeys = this.getAllKeys(items);
    var checkedKeys = this.state.checkedKeys;
    var checkedCount = allKeys.filter(function(key) {
      return checkedKeys.indexOf(key) > -1;
    }).length;
    return { checked: checkedCount, total: allKeys.length };
  };

  isModuleEnabled = (module) => {
    var stats = this.getModuleStats(module);
    return stats.checked > 0;
  };

  isModuleFullyChecked = (module) => {
    var stats = this.getModuleStats(module);
    return stats.total > 0 && stats.checked === stats.total;
  };

  selectModule = (module) => {
    this.setState({ selectedModule: module });
  };

  toggleSection = (key) => {
    var expandedSections = Object.assign({}, this.state.expandedSections);
    expandedSections[key] = !expandedSections[key];
    this.setState({ expandedSections: expandedSections });
  };

  // ─── Árvore de permissões (antd Tree, checkbox tri-state nativo) ──────────

  renderTreeNodes = (items) => {
    var self = this;

    return items.map(function(item) {
      var isParent = !!(item.children && item.children.length > 0);

      if (isParent) {
        var stats = self.getModuleStats(item);
        var isFullyChecked = stats.total > 0 && stats.checked === stats.total;
        var isPartiallyChecked = stats.checked > 0 && stats.checked < stats.total;

        var parentTitle = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {item.icon && <Icon type={item.icon} style={{ color: '#595959' }} />}
            <span style={{ fontWeight: 500 }}>{item.title}</span>
            <Badge count={stats.checked + '/' + stats.total} style={{
              backgroundColor: isFullyChecked ? '#52c41a' : isPartiallyChecked ? '#faad14' : '#d9d9d9',
              fontSize: 11
            }} />
          </span>
        );

        return (
          <Tree.TreeNode key={item.key} title={parentTitle}>
            {self.renderTreeNodes(item.children)}
          </Tree.TreeNode>
        );
      }

      var leafTitle = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {item.icon && <Icon type={item.icon} style={{ color: '#8c8c8c' }} />}
          {item.title}
          {item.info && (
            <Tooltip title={item.info} placement="top">
              <Icon type="info-circle" style={{ fontSize: 13, color: '#bfbfbf', cursor: 'help' }} />
            </Tooltip>
          )}
        </span>
      );

      return <Tree.TreeNode key={item.key} title={leafTitle} />;
    });
  };

  // O antd devolve as chaves marcadas incluindo pais totalmente marcados —
  // filtramos para permissões reais e fundimos com as dos outros módulos.
  onTreeCheck = (checkedKeysArg) => {
    var selectedModule = this.state.selectedModule;
    if (!selectedModule) return;

    var moduleLeafKeys = this.getAllKeys([selectedModule]);
    var checkedNow = (checkedKeysArg || []).filter(function(key) {
      return key.indexOf('_PARENT') === -1 && key.indexOf('_SECTION') === -1;
    });

    var others = this.state.checkedKeys.filter(function(key) {
      return moduleLeafKeys.indexOf(key) === -1;
    });

    this.setState({ checkedKeys: others.concat(checkedNow) });
  };

  onCreate = () => {
    var form = this.props.form;
    var checkedKeys = this.state.checkedKeys;
    var self = this;

    form.validateFields(async function(err, values) {
      if (err) {
        return;
      }

      values.permissions = checkedKeys.filter(function(item) {
        return item.indexOf('_PARENT') === -1 && item.indexOf('_SECTION') === -1;
      });

      if (values.permissions.length === 0) {
        form.setFields({
          name: {
            value: values.name,
            errors: [new Error('Selecione pelo menos uma permissão!')]
          }
        });
        return;
      }

      self.setState({ isSaving: true });
      try {
        var data = self.props.modal.data;

        if (data) {
          await self.props.updatePermissionsGroup(data.id, values);
        } else {
          await self.props.createPermissionsGroup(values);
        }

        self.props.closeModal();
      } finally {
        self.setState({ isSaving: false });
      }
    });
  };

  render() {
    var closeModal = this.props.closeModal;
    var form = this.props.form;
    var user = this.props.user;
    var isPremiumPlan = user.tenant.plan === 'premium';
    var allowBetaFeatures = user.tenant.is_beta_tester;
    var getFieldDecorator = form.getFieldDecorator;
    var isVisible = this.props.modal.isVisible;
    var data = this.props.modal.data;
    var isSaving = this.state.isSaving;
    var selectedModule = this.state.selectedModule;
    var self = this;

    var modules = this.getTopLevelModules(permissionsTree, isPremiumPlan, allowBetaFeatures);
    var isSocio = data && data.id === 1;

    // Calculate total stats
    var totalStats = { checked: 0, total: 0 };
    modules.forEach(function(module) {
      var stats = self.getModuleStats(module);
      totalStats.checked += stats.checked;
      totalStats.total += stats.total;
    });

    return (
      <Modal
        visible={isVisible}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon type="safety-certificate" style={{ fontSize: 20, color: '#1890ff' }} />
            <span>{data ? 'Editar grupo de permissões' : 'Novo grupo de permissões'}</span>
          </div>
        }
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width="100vw"
        zIndex={1010}
        style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
        bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 55px)' }}
      >
        {/* Name Input */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <Form layout="vertical" style={{ marginBottom: 0 }}>
            <FormItem label="Nome do grupo" style={{ marginBottom: 0 }}>
              {getFieldDecorator('name', {
                initialValue: data ? data.name : undefined,
                rules: [{ required: true, message: 'Por favor informe o nome!' }],
              })(
                <Input
                  placeholder="Ex: Financeiro, Administrativo, Contador..."
                  size="large"
                  prefix={<Icon type="team" style={{ color: '#bfbfbf' }} />}
                  disabled={isSocio}
                />
              )}
            </FormItem>
          </Form>
        </div>

        {/* Sócio locked banner */}
        {isSocio && (
          <div style={{
            padding: '12px 24px',
            background: '#f6ffed',
            borderBottom: '1px solid #b7eb8f',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Icon type="lock" style={{ color: '#52c41a' }} />
            <span style={{ fontSize: 13, color: '#389e0d' }}>
              O grupo Sócio possui acesso total e não pode ser editado.
            </span>
          </div>
        )}

        {/* Split View */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left Panel - Module List */}
          <div style={{
            width: 320,
            borderRight: '1px solid #f0f0f0',
            overflowY: 'auto',
            background: '#fafafa'
          }}>
            <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>Módulos</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {totalStats.checked} de {totalStats.total} permissões ativas
              </div>
            </div>

            {modules.map(function(module) {
              var stats = self.getModuleStats(module);
              var isEnabled = stats.checked > 0;
              var isFullyChecked = stats.total > 0 && stats.checked === stats.total;
              var isSelected = selectedModule && selectedModule.key === module.key;

              return (
                <div
                  key={module.key}
                  onClick={function() { self.selectModule(module); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    background: isSelected ? '#e6f7ff' : '#fff',
                    borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                    borderBottom: '1px solid #f5f5f5',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: isFullyChecked ? '#f6ffed' : isEnabled ? '#e6f7ff' : '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isFullyChecked ? (
                        <Icon
                          type="check-circle"
                          theme="filled"
                          style={{ fontSize: 16, color: '#52c41a' }}
                        />
                      ) : (
                        <Icon
                          type={module.icon || 'appstore'}
                          style={{ fontSize: 16, color: isEnabled ? '#1890ff' : '#bfbfbf' }}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 500,
                        fontSize: 13,
                        color: '#262626',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {module.title}
                        {module.isBeta && (
                          <Badge count="BETA" style={{ marginLeft: 6, backgroundColor: '#722ed1', fontSize: 9 }} />
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: isFullyChecked ? '#52c41a' : '#8c8c8c' }}>
                        {isFullyChecked ? (
                          <span><Icon type="check" style={{ fontSize: 10, marginRight: 4 }} />Acesso total</span>
                        ) : (
                          <span>{stats.checked}/{stats.total} permissões</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Panel - Module Details */}
          <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
            {selectedModule ? (
              <div>
                {/* Module Header */}
                <div style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #f0f0f0',
                  background: '#fafafa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        background: '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon type={selectedModule.icon || 'appstore'} style={{ fontSize: 22, color: '#1890ff' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 16, color: '#262626' }}>
                          {selectedModule.title}
                        </div>
                        {selectedModule.description && (
                          <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 2 }}>
                            {selectedModule.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: '#595959' }}>Selecionar todos</span>
                      <Switch
                        checked={this.isModuleFullyChecked(selectedModule)}
                        disabled={isSocio}
                        onChange={function(checked) { self.toggleAllInModule(selectedModule, checked); }}
                      />
                    </div>
                  </div>
                </div>

                {/* Permissions Tree */}
                <div style={{ padding: '16px 24px' }}>
                  {selectedModule.children ? (
                    <Tree
                      key={selectedModule.key}
                      checkable
                      selectable={false}
                      defaultExpandAll
                      disabled={isSocio}
                      checkedKeys={this.state.checkedKeys.filter(function(key) {
                        return self.getAllKeys([selectedModule]).indexOf(key) > -1;
                      })}
                      onCheck={this.onTreeCheck}
                    >
                      {this.renderTreeNodes(selectedModule.children)}
                    </Tree>
                  ) : (
                    <div style={{
                      padding: '12px',
                      background: this.isChecked(selectedModule.key) ? '#f6ffed' : '#fafafa',
                      borderRadius: 8,
                      border: '1px solid ' + (this.isChecked(selectedModule.key) ? '#b7eb8f' : '#e8e8e8'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Acesso ao módulo
                        {selectedModule.info && (
                          <Tooltip title={selectedModule.info} placement="top">
                            <Icon type="info-circle" style={{ fontSize: 13, color: '#bfbfbf', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </span>
                      <Switch
                        checked={this.isChecked(selectedModule.key)}
                        disabled={isSocio}
                        onChange={function() { self.togglePermission(selectedModule.key); }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#bfbfbf'
              }}>
                <Icon type="select" style={{ fontSize: 48, marginBottom: 16 }} />
                <div style={{ fontSize: 14 }}>Selecione um módulo para configurar</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Clique em um módulo na lista à esquerda</div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 13, color: '#8c8c8c' }}>
            {totalStats.checked} de {totalStats.total} permissões ativas
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isSocio && (
              <Button size="large" onClick={closeModal}>Cancelar</Button>
            )}
            <Button
              type="primary"
              size="large"
              loading={isSaving}
              onClick={isSocio ? closeModal : this.onCreate}
            >
              {isSocio ? 'Fechar' : (data ? 'Salvar alterações' : 'Criar grupo')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
}

const mapStateProps = (state) => ({
  user: state.user,
  modal: state.permissions.modal,
});

const mapDispatchProps = (dispatch) => ({
  closeModal: () => dispatch(permissionsActions.closeModal()),
  createPermissionsGroup: (data) => dispatch(permissionsActions.create(data)),
  updatePermissionsGroup: (id, data) =>
    dispatch(permissionsActions.update(id, data)),
});

export default connect(
  mapStateProps,
  mapDispatchProps
)(Form.create()(PermissionsFormModal));
